/**
 * Sprint 18 B2: No-Show Prediction — risk scores per upcoming booking
 * Color-coded: green <30%, yellow 30-60%, red >60%.
 */
import { useState } from "react";
import { AlertTriangle, CheckCircle, Bell, Calendar } from "lucide-react";
import { useApi } from "@/hooks/use-api";
import { apiFetch } from "@/lib/api-client";
import { formatDate } from "@/lib/i18n";
import { Skeleton } from "@/components/skeleton";

interface PredictionBooking {
  id: string;
  customerName: string;
  serviceName: string;
  startAt: string;
  noShowRisk: number;
  reminderSent: boolean;
  riskFactors: string[];
}

interface NoShowRiskFactor {
  key: string;
  label: string;
  value: boolean | number;
  impactPercent: number;
  active: boolean;
}

interface NoShowRiskDetail {
  bookingId: string;
  risk: number;
  badge: "LOW" | "MEDIUM" | "HIGH" | "VERY_HIGH";
  factors: NoShowRiskFactor[];
}

const BADGE_STYLES: Record<NoShowRiskDetail["badge"], string> = {
  LOW: "bg-[var(--color-success)]/12 text-[var(--color-success)] border-[var(--color-success)]/25",
  MEDIUM: "bg-[var(--color-warning)]/12 text-[var(--color-warning)] border-[var(--color-warning)]/25",
  HIGH: "bg-[var(--color-warning)]/16 text-[var(--color-warning)] border-[var(--color-warning)]/35",
  VERY_HIGH: "bg-[var(--color-error)]/12 text-[var(--color-error)] border-[var(--color-error)]/25",
};

function riskColor(risk: number) {
  if (risk > 60) return { bg: "bg-[var(--color-error)]/10", text: "text-[var(--color-error)]", border: "border-[var(--color-error)]/30" };
  if (risk > 30) return { bg: "bg-[var(--color-warning)]/10", text: "text-[var(--color-warning)]", border: "border-[var(--color-warning)]/30" };
  return { bg: "bg-[var(--color-success)]/10", text: "text-[var(--color-success)]", border: "border-[var(--color-success)]/30" };
}

export default function BookingPredictionsPage() {
  const { data, isLoading, error } = useApi<{ bookings: PredictionBooking[] }>("/crm/bookings/predictions");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detailsById, setDetailsById] = useState<Record<string, NoShowRiskDetail>>({});
  const [detailErrorById, setDetailErrorById] = useState<Record<string, string>>({});
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const bookings = data?.bookings ?? [];

  async function toggleDetails(bookingId: string) {
    if (expandedId === bookingId) {
      setExpandedId(null);
      return;
    }

    setExpandedId(bookingId);
    if (detailsById[bookingId] || loadingId === bookingId) return;

    setLoadingId(bookingId);
    setDetailErrorById((current) => ({ ...current, [bookingId]: "" }));

    try {
      const response = await apiFetch<{ data: NoShowRiskDetail }>(`/crm/bookings/${bookingId}/no-show-risk`);
      setDetailsById((current) => ({ ...current, [bookingId]: response.data }));
    } catch (caughtError) {
      setDetailErrorById((current) => ({
        ...current,
        [bookingId]: caughtError instanceof Error ? caughtError.message : "Failed to load no-show risk details.",
      }));
    } finally {
      setLoadingId((current) => (current === bookingId ? null : current));
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">No-Show Predictions</h1>
        <p className="text-sm text-[var(--color-muted-foreground)]">AI-predicted risk scores for upcoming bookings.</p>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
      ) : error ? (
        <div className="rounded-xl border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 p-5 text-sm text-[var(--color-foreground)]">
          No-show predictions stay hidden until the mounted predictions contract is available. This page no longer fabricates risk scores from mock bookings.
        </div>
      ) : bookings.length === 0 ? (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5 text-sm text-[var(--color-muted-foreground)]">
          No prediction rows are available yet. Booking risk cards will appear here once the scheduling prediction service starts returning live results.
        </div>
      ) : (
        <div className="space-y-3">
          {bookings.map((b) => {
            const colors = riskColor(b.noShowRisk);
            const detail = detailsById[b.id];
            return (
              <div key={b.id} className={`stagger-item rounded-xl border ${colors.border} ${colors.bg} p-5`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`text-2xl font-bold ${colors.text}`}>{b.noShowRisk}%</div>
                    <div>
                      <div className="font-medium text-[var(--color-foreground)]">{b.customerName}</div>
                      <div className="text-sm text-[var(--color-muted-foreground)] flex items-center gap-2">
                        <span>{b.serviceName}</span>
                        <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {formatDate(b.startAt)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {detail && (
                      <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold tracking-[0.14em] ${BADGE_STYLES[detail.badge]}`}>
                        {detail.badge.replace("_", " ")}
                      </span>
                    )}
                    {b.reminderSent ? (
                      <span className="flex items-center gap-1 text-xs text-[var(--color-success)]"><CheckCircle className="h-3.5 w-3.5" /> Reminded</span>
                    ) : (
                      <span className="flex items-center gap-1 rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-xs text-[var(--color-muted-foreground)]">
                        <Bell className="h-3.5 w-3.5" /> Reminder not sent
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => void toggleDetails(b.id)}
                      className="rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-xs font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
                    >
                      {expandedId === b.id ? "Hide factors" : "View factors"}
                    </button>
                  </div>
                </div>
                {b.riskFactors.length > 0 && (
                  <div className="flex items-center gap-2 mt-3 text-xs text-[var(--color-muted-foreground)]">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    {b.riskFactors.join(" · ")}
                  </div>
                )}
                {expandedId === b.id && (
                  <div className="mt-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-background)]/70 p-4">
                    {loadingId === b.id ? (
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-32 rounded-md" />
                        <Skeleton className="h-4 w-full rounded-md" />
                        <Skeleton className="h-4 w-5/6 rounded-md" />
                      </div>
                    ) : detailErrorById[b.id] ? (
                      <p className="text-sm text-[var(--color-error)]">{detailErrorById[b.id]}</p>
                    ) : detail ? (
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm font-medium text-[var(--color-foreground)]">Weighted no-show factors</p>
                          <p className="text-xs text-[var(--color-muted-foreground)]">
                            Real booking-specific model output from the mounted CRM booking route.
                          </p>
                        </div>
                        <div className="grid gap-2">
                          {detail.factors.map((factor) => (
                            <div key={factor.key} className="flex items-center justify-between rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm">
                              <div>
                                <p className="font-medium text-[var(--color-foreground)]">{factor.label}</p>
                                <p className="text-xs text-[var(--color-muted-foreground)]">
                                  Value: {typeof factor.value === "boolean" ? (factor.value ? "Yes" : "No") : factor.value}
                                </p>
                              </div>
                              <span className={factor.impactPercent === 0 ? "text-[var(--color-muted-foreground)]" : factor.impactPercent > 0 ? "text-[var(--color-error)]" : "text-[var(--color-success)]"}>
                                {factor.impactPercent > 0 ? `+${factor.impactPercent}` : factor.impactPercent}%
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
